import { supabase } from '@/integrations/supabase/client';
import { devLog } from '@/utils/logger';

export interface RollbackOptions {
  runId: string;
  reason: string;
  performedBy: string;
}

export interface RollbackResult {
  success: boolean;
  deletedPayslips: number;
  reversedAccruals: number; // NEW: Count of reversed leave accruals
  error?: string;
}

interface RollbackRpcResponse {
  success: boolean;
  deleted_payslips?: number;
  error?: string;
}

interface AccrualReversalResponse {
  success: boolean;
  reversed_count: number;
  total_days_reversed?: number;
  total_hours_reversed?: number;
  error?: string;
}

export interface IndividualRollbackOptions {
  payslipIds: string[];
  reason: string;
  performedBy: string;
}

export interface IndividualRollbackResult {
  success: boolean;
  deletedPayslips: number;
  reversedAccruals: number;
  remainingPayslips: number;
  runStatus: 'completed' | 'rolled_back';
  error?: string;
}

interface IndividualRollbackRpcResponse {
  success: boolean;
  deleted_payslips?: number;
  reversed_accruals?: number;
  remaining_payslips?: number;
  run_status?: string;
  error?: string;
}

export const payrollRollbackService = {
  /**
   * Rollback individual payslips from a completed payroll run
   * Reverses leave accruals, deletes payslips, and updates run totals
   */
  async rollbackIndividualPayslips(options: IndividualRollbackOptions): Promise<IndividualRollbackResult> {
    try {
      const { payslipIds, reason, performedBy } = options;

      const { data, error } = await supabase.rpc('rollback_individual_payslips', {
        p_payslip_ids: payslipIds,
        p_reason: reason,
        p_performed_by: performedBy,
      });

      if (error) {
        throw new Error(error.message);
      }

      const result = data as unknown as IndividualRollbackRpcResponse;

      if (!result || !result.success) {
        throw new Error(result?.error || 'Individual payslip rollback failed');
      }

      return {
        success: true,
        deletedPayslips: result.deleted_payslips || 0,
        reversedAccruals: result.reversed_accruals || 0,
        remainingPayslips: result.remaining_payslips || 0,
        runStatus: result.run_status === 'rolled_back' ? 'rolled_back' : 'completed',
      };
    } catch (error) {
      console.error('Individual payslip rollback failed:', error);
      return {
        success: false,
        deletedPayslips: 0,
        reversedAccruals: 0,
        remainingPayslips: 0,
        runStatus: 'completed',
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  /**
   * Rollback a completed payroll run
   * Reverses leave accruals first, then deletes payslips and marks run as rolled back
   */
  async rollbackPayrollRun(options: RollbackOptions): Promise<RollbackResult> {
    try {
      const { runId, reason, performedBy } = options;

      // PHASE 3: Step 1 - Reverse leave accruals FIRST (before deleting payslips)
      const { data: accrualReversal, error: accrualError } = await supabase.rpc(
        'rollback_payroll_leave_accruals',
        {
          p_run_id: runId,
          p_performed_by: performedBy,
        }
      );

      if (accrualError) {
        console.error('Failed to reverse leave accruals:', accrualError);
        throw new Error(`Accrual reversal failed: ${accrualError.message}`);
      }

      const accrualResult = accrualReversal as unknown as AccrualReversalResponse;

      if (!accrualResult.success) {
        throw new Error(accrualResult.error || 'Accrual reversal failed');
      }

      devLog(`Reversed ${accrualResult.reversed_count} leave accrual(s)`);

      // Step 2: Call existing rollback function (deletes payslips)
      const { data, error } = await supabase.rpc('rollback_payroll_run', {
        p_run_id: runId,
        p_reason: reason,
        p_performed_by: performedBy,
      });

      if (error) {
        throw new Error(error.message);
      }

      // Type cast the response (cast through unknown to satisfy TypeScript)
      const result = data as unknown as RollbackRpcResponse;

      // The function returns a JSON object with success status
      if (!result || !result.success) {
        throw new Error(result?.error || 'Rollback failed');
      }

      return {
        success: true,
        deletedPayslips: result.deleted_payslips || 0,
        reversedAccruals: accrualResult.reversed_count || 0,
      };
    } catch (error) {
      console.error('Rollback failed:', error);
      return {
        success: false,
        deletedPayslips: 0,
        reversedAccruals: 0,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  /**
   * Validate if a run can be rolled back
   */
  async canRollback(runId: string): Promise<{ canRollback: boolean; reason?: string }> {
    try {
      const { data: run, error } = await supabase
        .from('payroll_runs')
        .select('status')
        .eq('id', runId)
        .single();

      if (error || !run) {
        return { canRollback: false, reason: 'Run not found' };
      }

      if (run.status !== 'completed') {
        return { 
          canRollback: false, 
          reason: `Cannot rollback run with status: ${run.status}` 
        };
      }

      return { canRollback: true };
    } catch (error) {
      return { 
        canRollback: false, 
        reason: 'Failed to validate rollback eligibility' 
      };
    }
  },
};
